/** Operational surfaces use a `YYYY-MM` month string as their shared lens. */

export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function isValidMonth(value: string): boolean {
  return /^\d{4}-\d{2}$/.test(value);
}

/** `2026-06` → `2026-06-01` (the API stores FX/budgets at month-start). */
export function monthStart(month: string): string {
  return `${month}-01`;
}

/** Last calendar day of `YYYY-MM`, as `YYYY-MM-DD`. */
export function monthEnd(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y!, m!, 0);
  const day = String(d.getDate()).padStart(2, '0');
  const monthNum = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${monthNum}-${day}`;
}

/** Local calendar date as `YYYY-MM-DD`. */
export function todayIso(now = new Date()): string {
  const day = String(now.getDate()).padStart(2, '0');
  const monthNum = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${monthNum}-${day}`;
}

/**
 * Date a balance report closes on. The month in progress uses today;
 * any other month uses its last day.
 */
export function asOfForMonth(month: string, today = todayIso()): string {
  const end = monthEnd(month);
  if (today.slice(0, 7) === month && today < end) return today;
  return end;
}

export function addMonths(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Inclusive list of `count` months ending at `endMonth`, oldest first. */
export function recentMonths(endMonth: string, count: number): string[] {
  const months: string[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    months.push(addMonths(endMonth, -i));
  }
  return months;
}

export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
}

export function monthLabelShort(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('default', { month: 'short', year: '2-digit' });
}
